import type { AdminVehicleCatalogModelDto } from '@carcheck/shared';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import type { ModelFlag } from '../_lib/useModelToggle';

/**
 * The make's models (AUT-437): a plain MUI table over the already-filtered
 * rows (not `AdminDataGrid` — that one is server-paginated). Flag chips
 * save on click; «…» opens the model dialog. «Поколения» (AUT-450) is the
 * model's ACTIVE `generationCount` («—» for none) — a click opens the dialog
 * too.
 */
const FLAGS: readonly ModelFlag[] = ['popular', 'onSale', 'legacy', 'hidden'];
const FLAG_COLOR = {
  popular: 'warning',
  onSale: 'success',
  legacy: 'default',
  hidden: 'default',
} as const;

const isOn = (m: AdminVehicleCatalogModelDto, flag: ModelFlag): boolean =>
  flag === 'hidden' ? m.status === 'hidden' : m[flag];

export interface ModelsTableProps {
  models: readonly AdminVehicleCatalogModelDto[];
  disabled: boolean;
  onToggle: (model: AdminVehicleCatalogModelDto, flag: ModelFlag) => void;
  onEdit: (model: AdminVehicleCatalogModelDto) => void;
}

const ModelRow = ({
  model,
  disabled,
  onToggle,
  onEdit,
}: Omit<ModelsTableProps, 'models'> & { model: AdminVehicleCatalogModelDto }): ReactElement => {
  const { t } = useTranslation();
  return (
    <TableRow hover sx={{ opacity: model.status === 'hidden' ? 0.6 : 1 }}>
      <TableCell>
        <Typography variant="body2" fontWeight={600}>
          {model.name}
        </Typography>
        {model.aliases.length > 0 ? (
          <Typography variant="caption" color="text.secondary" fontFamily="monospace">
            {model.aliases.map((a) => a.alias).join(', ')}
          </Typography>
        ) : null}
      </TableCell>
      <TableCell>
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          {FLAGS.map((flag) => {
            const on = isOn(model, flag);
            const label = t(`vehicleCatalog.flags.${flag}`);
            return (
              <Chip
                key={flag}
                size="small"
                label={label}
                color={on ? FLAG_COLOR[flag] : 'default'}
                variant={on ? 'filled' : 'outlined'}
                disabled={disabled}
                aria-pressed={on}
                aria-label={t('vehicleCatalog.models.toggle', { flag: label, name: model.name })}
                onClick={() => onToggle(model, flag)}
              />
            );
          })}
        </Box>
      </TableCell>
      <TableCell align="right" sx={{ fontFamily: 'monospace' }}>
        <Link
          component="button"
          type="button"
          underline="hover"
          color={model.generationCount > 0 ? 'primary' : 'text.secondary'}
          aria-label={t('vehicleCatalog.models.generationsAria', { name: model.name })}
          onClick={() => onEdit(model)}
          sx={{ fontFamily: 'monospace' }}
        >
          {model.generationCount > 0 ? model.generationCount : t('vehicleCatalog.noValue')}
        </Link>
      </TableCell>
      <TableCell align="right" sx={{ fontFamily: 'monospace' }}>
        {model.usageCount.toLocaleString('ru-RU')}
      </TableCell>
      <TableCell padding="checkbox">
        <IconButton
          size="small"
          aria-label={t('vehicleCatalog.models.edit', { name: model.name })}
          onClick={() => onEdit(model)}
        >
          <MoreHorizIcon fontSize="small" />
        </IconButton>
      </TableCell>
    </TableRow>
  );
};

export const ModelsTable = ({ models, ...rest }: ModelsTableProps): ReactElement => {
  const { t } = useTranslation();
  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>{t('vehicleCatalog.models.columns.model')}</TableCell>
          <TableCell>{t('vehicleCatalog.models.columns.flags')}</TableCell>
          <TableCell align="right">{t('vehicleCatalog.models.columns.generations')}</TableCell>
          <TableCell align="right">{t('vehicleCatalog.models.columns.usage')}</TableCell>
          <TableCell padding="checkbox" />
        </TableRow>
      </TableHead>
      <TableBody>
        {models.map((m) => (
          <ModelRow key={m.id} model={m} {...rest} />
        ))}
      </TableBody>
    </Table>
  );
};
